import { useQuery } from '@tanstack/react-query';
import type { LedgerEntry } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { Badge } from '@/components/Badge';
import { fetchLedger, reportKeys } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

/** Settlements are money IN (credit), reversals are money OUT (debit) — sign kept only here, at the edge. */
function signedAmount(entry: LedgerEntry): number {
  return entry.kind === 'settlement' ? entry.amount : -entry.amount;
}

const FILTER_FIELDS = ['Customer', 'Reference', 'Type'] as const;

const TOGGLEABLE = ['kind', 'customerName', 'ref', 'amount'] as const;

const COLUMN_LABELS: Record<string, string> = {
  kind: 'Type',
  customerName: 'Customer',
  ref: 'Reference',
  amount: 'Amount',
};

const LEDGER_COLUMNS: Column<LedgerEntry>[] = [
  { key: 'date', header: 'Date', pinned: true, cell: (row) => formatDate(row.date) },
  {
    key: 'kind',
    header: 'Type',
    cell: (row) =>
      row.kind === 'settlement' ? (
        <Badge tone="success" dot>
          Settlement
        </Badge>
      ) : (
        <Badge tone="danger" dot>
          Reversal
        </Badge>
      ),
  },
  { key: 'customerName', header: 'Customer', cell: (row) => row.customerName },
  { key: 'ref', header: 'Reference', cell: (row) => row.ref },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    cell: (row) => `${row.kind === 'reversal' ? '-' : ''}${money(row.amount)}`,
  },
];

/** Running total = sum(settlements) - sum(reversals), computed client-side over the visible rows. */
function runningTotal(rows: readonly LedgerEntry[]): number {
  return rows.reduce((acc, row) => acc + signedAmount(row), 0);
}

export function LedgerPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const layout = useReportLayout('ledger', TOGGLEABLE);
  const columns = applyReportLayout(LEDGER_COLUMNS, layout);

  const query = useQuery({
    queryKey: reportKeys.ledger(filters),
    queryFn: ({ signal }) => fetchLedger(filters, signal),
  });

  return (
    <div>
      <PageHeader title="Ledger" description="Credit-sale settlements and sale returns for the selected window." />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search customer or reference"
        />
        <ReportFiltersButton
          fields={FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) =>
            field === 'Type' ? 'settlement / reversal' : field === 'Customer' ? 'name' : 'reference'
          }
        />
        <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
        <ReportPeriodButton
          from={filters.from}
          to={filters.to}
          onChange={toolbar.setRange}
          deselected={toolbar.filterActive}
        />
      </ReportToolbar>
      <AsyncSection
        state={query}
        // Whole result set comes back, so the toolbar's predicate applies
        // here rather than in the query (see applyClientFilters).
        select={(rows: LedgerEntry[]) =>
          applyClientFilters(rows, {
            search: toolbar.search,
            filterRows: toolbar.filterRows,
            matchAny: toolbar.matchAny,
            fieldValue: (row, field) =>
              field === 'Customer'
                ? row.customerName
                : field === 'Reference'
                  ? row.ref
                  : field === 'Type'
                    ? row.kind
                    : undefined,
            searchValues: (row) => [row.customerName, row.ref],
          })
        }
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="No ledger activity in this window"
        skeleton={<TableSkeleton columns={5} />}
      >
        {(rows) => (
          <DataTable
            caption="Ledger"
            layout={layout}
            columns={columns}
            rows={rows}
            rowKey={(row, index) => `${row.ref}-${index}`}
            totals={{ label: 'Running total', cells: { amount: money(runningTotal(rows)) } }}
          />
        )}
      </AsyncSection>
    </div>
  );
}
